/**
 * Dark or light. Dark is the default (the owner's choice, 2026-09-27); a
 * signed-in person's choice is kept on their account, so it follows them to
 * every device they sign in on, and "match my device" is there for anyone who
 * wants their system setting. The choice is set as `data-theme` on <html>;
 * styles.css defines the dark colours for data-theme="dark" and for "device
 * dark, nothing else chosen".
 *
 * The last choice is also cached in localStorage, and index.html applies it
 * before the first paint - so a page opens in the right colours at once, and
 * the account's choice replaces it as soon as the account has loaded.
 *
 * Only the screen changes: the print page, the PDF and the folding pictures
 * draw paper in fixed colours.
 */
import { useEffect, useState } from 'react';
import { t } from './strings.ts';

export type ThemeChoice = 'dark' | 'light' | 'system';
const KEY = 'fmc.theme';
export const DEFAULT_THEME: ThemeChoice = 'dark';
const isChoice = (v: unknown): v is ThemeChoice => v === 'dark' || v === 'light' || v === 'system';

function cached(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return isChoice(v) ? v : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

/* one choice for the whole page, whichever switch changed it */
let current: ThemeChoice = cached();
const listeners = new Set<(c: ThemeChoice) => void>();
let saveToAccount: ((c: ThemeChoice) => void) | null = null;

function set(choice: ThemeChoice, save: boolean) {
  current = choice;
  if (choice === 'system') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = choice;
  try { localStorage.setItem(KEY, choice); } catch { /* the account still has it */ }
  for (const l of listeners) l(choice);
  if (save) saveToAccount?.(choice);
}

/** The signed-in account's choice has arrived: show it (it is already saved). */
export function adoptTheme(choice: string) {
  if (isChoice(choice)) set(choice, false);
}

/** Where a change is saved while someone is signed in; null when nobody is. */
export function setThemeSaver(save: ((c: ThemeChoice) => void) | null) {
  saveToAccount = save;
}

export function useTheme(): [ThemeChoice, (c: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(current);
  useEffect(() => {
    listeners.add(setChoice);
    // another tab changed it
    const sync = (e: StorageEvent) => { if (e.key === KEY && isChoice(e.newValue)) set(e.newValue, false); };
    addEventListener('storage', sync);
    return () => { listeners.delete(setChoice); removeEventListener('storage', sync); };
  }, []);
  return [choice, (c: ThemeChoice) => set(c, true)];
}

const OPTIONS: { value: ThemeChoice; icon: string; label: 'themeDark' | 'themeLight' | 'themeSystem' }[] = [
  { value: 'dark', icon: '☾', label: 'themeDark' },
  { value: 'light', icon: '☀', label: 'themeLight' },
  { value: 'system', icon: '◐', label: 'themeSystem' },
];

/** The three-way switch. `compact` shows icons only, for crowded bars. */
export function ThemeSwitch({ compact = false }: { compact?: boolean }) {
  const [choice, setChoice] = useTheme();
  return (
    <span className={`seg theme-switch${compact ? ' compact' : ''}`} role="group" aria-label={t('theme')}>
      {OPTIONS.map((o) => (
        <button key={o.value} type="button" aria-pressed={choice === o.value} onClick={() => setChoice(o.value)}
          title={t(o.label)} aria-label={compact ? t(o.label) : undefined}>
          <span aria-hidden="true">{o.icon}</span>{!compact && <> {t(o.label)}</>}
        </button>
      ))}
    </span>
  );
}
